import { Music } from "lucide-react";
import React, { useEffect, useState } from "react";

interface NowPlayingToastProps {
  stationName: string;
  artistName?: string;
  logoUrl?: string;
  isVisible: boolean;
}

const NowPlayingToast: React.FC<NowPlayingToastProps> = ({
  stationName,
  artistName,
  logoUrl,
  isVisible,
}) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Defer show to avoid synchronous state update warning
      const t1 = setTimeout(() => setShow(true), 0);
      const t2 = setTimeout(() => {
        setShow(false);
      }, 3000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
  }, [isVisible, stationName]);

  if (!show) return null;

  return (
    <div
      className={`fixed top-20 left-1/2 -translate-x-1/2 z-[90] transition-all duration-300 ${
        show ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
      }`}
    >
      <div className="flex items-center gap-3 px-4 py-2.5 bg-[#1a1a1a]/95 backdrop-blur-md border border-white/10 rounded-full shadow-xl">
        {/* Logo or Icon */}
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={stationName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[#22d3ee]/20 flex items-center justify-center">
            <Music className="w-4 h-4 text-[#22d3ee]" />
          </div>
        )}

        {/* Text */}
        <div className="flex flex-col">
          <span className="text-xs text-gray-400">Now Playing</span>
          <span className="text-sm font-semibold text-white truncate max-w-[200px]">
            {stationName}
          </span>
          {artistName && (
            <span className="text-xs text-gray-400 truncate max-w-[200px]">
              {artistName}
            </span>
          )}
        </div>

        {/* Playing indicator */}
        <div className="flex items-center gap-0.5 ml-2">
          <div
            className="w-0.5 h-3 bg-[#22d3ee] rounded-full animate-pulse"
            style={{ animationDelay: "0ms" }}
          />
          <div
            className="w-0.5 h-4 bg-[#22d3ee] rounded-full animate-pulse"
            style={{ animationDelay: "150ms" }}
          />
          <div
            className="w-0.5 h-2.5 bg-[#22d3ee] rounded-full animate-pulse"
            style={{ animationDelay: "300ms" }}
          />
        </div>
      </div>
    </div>
  );
};

export default NowPlayingToast;
